import { useCallback } from 'react';
import { getUserProperty } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { usePersistentlyCachedRequest } from '@atlassian/jira-platform-persistent-cache/src/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';

/**
 * Read-only hook to get user property as JSON object
 * @param property - The property key
 * @param userId - Optional user ID, defaults to current user
 * @returns The JSON object as type T, or undefined if not found/invalid
 */
export function useUserPropertyAsObject<T>(property: string, userId?: string): T | undefined {
	const { data: currentUser } = useCurrentUser();
	const user = userId || currentUser.user.accountId;

	const storageKey = `user-property-${user}-${property}`;

	const fetchUserProperty = useCallback(
		() =>
			user
				? getUserProperty<T>(user, property).catch((error) => {
						if (error instanceof FetchError && error.statusCode === 404) {
							return undefined;
						}
						throw error;
					})
				: undefined,
		[user, property],
	);

	const [value] = usePersistentlyCachedRequest<T | undefined>(
		storageKey,
		undefined,
		fetchUserProperty,
	);

	return value;
}
